import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse, Link } from "react-router";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";
import { reportError } from "@/lib/reportError";

export function RouteErrorBoundary() {
  const error = useRouteError();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!isNotFound && error instanceof Error) {
      reportError(error, { errorType: "route" });
    }
  }, [error, isNotFound]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gift-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gift-pink-500/10 rounded-full blur-3xl" />
      </div>

      <div className="text-center space-y-6 max-w-md">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-red-500/20 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-red-400" />
        </div>

        {isNotFound ? (
          <>
            <h1 className="text-4xl font-bold">Page Not Found</h1>
            <p className="text-muted-foreground">
              The page you're looking for doesn't exist or has been moved.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-4xl font-bold">Something Went Wrong</h1>
            <p className="text-muted-foreground">
              An unexpected error occurred. Please try again.
            </p>
            {error instanceof Error && (
              <pre className="text-xs text-red-400/70 bg-red-500/5 rounded-lg p-3 text-left overflow-auto max-h-32">
                {error.message}
              </pre>
            )}
          </>
        )}

        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" asChild>
            <Link to="/">
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Link>
          </Button>
          {!isNotFound && (
            <Button
              variant="gradient"
              onClick={() => window.location.reload()}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
